// pages/AWSCloudTrail.jsx — Log aktivitas & audit event AWS multi-region.
// Sumber data: GET /api/aws-cloudtrail (snapshot) & POST /api/aws-cloudtrail/refresh (fetch live).
// Refresh: window default 7 hari (write-events only), bisa di-expand (max 90 hari) & include read-only.
// Baris event bisa diklik → drawer kanan menampilkan detail JSON penuh (gaya AWS CloudTrail Console).
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import useTabDeepLink from '../hooks/useTabDeepLink';
import VpnTunnelLog, { VpnLogIcon } from '../components/VpnTunnelLog';
import UserActivity from '../components/UserActivity';
import {
  RefreshCw, ScrollText, Cloud, Clock, AlertTriangle, Search, ChevronRight,
  ChevronsUpDown, Globe2, User, ShieldAlert, Activity, ListFilter, X,
  Copy, Check, ChevronDown, Maximize2, Minimize2,
  UserCircle, Zap, MapPin, CheckCircle2, XCircle, Lock, Unlock, ShieldCheck, Info,
  Settings2, Calendar, Timer,
} from 'lucide-react';

// --- Multi-account AWS -----------------------------------------------------
// Akun aktif dipakai bersama halaman AWS Inventory (kunci localStorage sama),
// dan disisipkan otomatis sebagai ?account= pada tiap request /api/aws-cloudtrail*.
const CT_ACCOUNT_LS = 'awsinv.account';
let CT_ACCOUNT = 'primary';
try { CT_ACCOUNT = localStorage.getItem(CT_ACCOUNT_LS) || 'primary'; } catch (_) {}
const getCtAccount = () => CT_ACCOUNT;
const switchCtAccount = (key) => {
  CT_ACCOUNT = key;
  try { localStorage.setItem(CT_ACCOUNT_LS, key); } catch (_) {}
  window.location.reload();
};
api.interceptors.request.use((cfg) => {
  const u = String(cfg.url || '');
  if (u.startsWith('/api/aws-cloudtrail')) {
    cfg.params = Object.assign({}, cfg.params, { account: CT_ACCOUNT });
  }
  return cfg;
});

// Tab yang boleh datang dari `?tab=` (QuickSearch).
const CT_TABS = ['events', 'analytics', 'vpnlog', 'useractivity'];

const ISO_RE = /^\d{4}-\d{2}-\d{2}T/;

function fmtCell(val, locale) {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'string' && ISO_RE.test(val)) {
    const d = new Date(val);
    if (!isNaN(d)) return d.toLocaleString(locale, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  return String(val);
}

// Kolom tabel event (urutan tetap).
const COLUMNS = ['eventTime', 'eventName', 'eventSource', 'username', 'region', 'sourceIp'];
const FIELD_LABELS = {
  eventTime: 'Time', eventName: 'Event', eventSource: 'Source', username: 'User',
  region: 'Region', sourceIp: 'Source IP', userType: 'User Type', userAgent: 'User Agent',
  errorCode: 'Error', eventId: 'Event ID',
};

export default function AWSCloudTrail() {
  const {
    locale,
    t
  } = useLang();
  const id = locale !== 'en-US';

  const [snap, setSnap]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRef]  = useState(false);
  const [error, setError]     = useState(null);
  const [query, setQuery]     = useState('');
  const [selected, setSelected] = useState(null); // event terpilih → drawer kanan

  // Kontrol refresh (window & read-only).
  const [days, setDays] = useState(7);
  const [includeReadOnly, setIncludeReadOnly] = useState(false);

  const [sortCol, setSortCol] = useState('eventTime');
  const [sortDir, setSortDir] = useState('desc');
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const p = searchParams.get('tab');
    return CT_TABS.includes(p) ? p : 'events';
  }); // 'events' | 'analytics'
  useTabDeepLink('aws_cloudtrail', (p) => CT_TABS.includes(p), setActiveTab);
  const [drill, setDrill] = useState(null); // { title, rows } untuk drawer drill-through Analytics

  // Filter cepat.
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [regionFilter, setRegionFilter] = useState('__all__');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/aws-cloudtrail');
      setSnap(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(async () => {
    setRef(true); setError(null);
    try {
      const res = await api.post('/api/aws-cloudtrail/refresh',
        { days, includeReadOnly }, { timeout: 600000 });
      setSnap(res.data); setSelected(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setRef(false); }
  }, [days, includeReadOnly]);

  // Daftar akun AWS + setting region CloudTrail per akun.
  const [ctAccounts, setCtAccounts] = useState([]);
  const ctAccount = getCtAccount();
  const [ctSetOpen, setCtSetOpen] = useState(false);
  const [ctSet, setCtSet] = useState(null);
  const [ctSetSaving, setCtSetSaving] = useState(false);
  useEffect(() => {
    api.get('/api/aws-cloudtrail/accounts')
      .then((r) => setCtAccounts((r.data && r.data.accounts) || []))
      .catch(() => setCtAccounts([]));
  }, []);
  const openCtSettings = async () => {
    setCtSetOpen(true);
    setCtSet(null);
    try {
      const r = await api.get('/api/aws-cloudtrail/settings');
      setCtSet({ regions: r.data.regions || [], availableRegions: r.data.availableRegions || [] });
    } catch (_) {
      setCtSet({ regions: [], availableRegions: [] });
    }
  };
  const toggleCtRegion = (rg) => setCtSet((s) => ({
    ...s,
    regions: s.regions.includes(rg) ? s.regions.filter((x) => x !== rg) : s.regions.concat(rg),
  }));
  const saveCtSettings = async () => {
    setCtSetSaving(true);
    try {
      await api.put('/api/aws-cloudtrail/settings', { regions: ctSet.regions });
      setCtSetOpen(false);
    } catch (e) {
      alert('Failed to save CloudTrail settings.');
    }
    setCtSetSaving(false);
  };

  const events = snap?.events || [];
  const meta   = snap?.meta || null;
  const summary = snap?.summary || {};
  const lastRefreshed = snap?.lastRefreshed
    ? new Date(snap.lastRefreshed).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
    : null;
  const hasData = events.length > 0;

  const regionOptions = useMemo(() => {
    const set = new Set(events.map((e) => e.region).filter(Boolean));
    return [...set].sort();
  }, [events]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir(col === 'eventTime' ? 'desc' : 'asc'); }
  };

  // ── Aksi klik KPI card ──
  const resetFilters = useCallback(() => {
    setQuery(''); setErrorsOnly(false); setRegionFilter('__all__');
  }, []);
  const filterErrorsOnly = useCallback(() => {
    setQuery(''); setRegionFilter('__all__'); setErrorsOnly(true);
  }, []);
  const filterTopEvent = useCallback((name) => {
    if (!name) return;
    setErrorsOnly(false); setRegionFilter('__all__'); setQuery(name);
  }, []);
  // Status filter aktif (untuk highlight card).
  const activeKpi = errorsOnly ? 'failed'
    : (query && query === (summary.topEventNames?.[0]?.name)) ? 'top'
    : (!query && !errorsOnly && regionFilter === '__all__') ? 'total'
    : null;

  const filtered = useMemo(() => {
    let rows = events;
    if (errorsOnly) rows = rows.filter((e) => e.errorCode);
    if (regionFilter !== '__all__') rows = rows.filter((e) => e.region === regionFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter((e) =>
        COLUMNS.some((c) => String(e[c] ?? '').toLowerCase().includes(q)) ||
        String(e.errorCode ?? '').toLowerCase().includes(q));
    }
    return rows;
  }, [events, query, errorsOnly, regionFilter]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  // Tren per hari (atau per jam bila window <= 2 hari) untuk sparkline KPI.
  const trend = useMemo(() => {
    const byHour = (meta?.days ?? 7) <= 2;
    const all = new Map(); const bad = new Map();
    events.forEach((e) => {
      const s = e.eventTime || '';
      if (!ISO_RE.test(s)) return;
      const k = byHour ? s.slice(0, 13) : s.slice(0, 10);
      all.set(k, (all.get(k) || 0) + 1);
      if (e.errorCode) bad.set(k, (bad.get(k) || 0) + 1);
    });
    const keys = [...all.keys()].sort();
    return { all: keys.map((k) => all.get(k)), failed: keys.map((k) => bad.get(k) || 0) };
  }, [events, meta]);
  const failedTotal = useMemo(() => events.filter((e) => e.errorCode).length, [events]);

  const TABS = [
    { key: 'events',       label: 'Events',            icon: ListFilter, count: events.length },
    { key: 'analytics',    label: t('awsTrail.analytics'), icon: Activity },
    { key: 'vpnlog',       label: 'VPN Tunnel Log',    icon: VpnLogIcon },
    { key: 'useractivity', label: 'User Activity',     icon: UserCircle },
  ];

  const chip = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.08] ring-1 ring-inset ring-white/10 text-xs font-medium text-white/90';
  const ctrl = 'bg-white/[0.08] ring-1 ring-inset ring-white/10 hover:bg-white/[0.13] transition-colors';

  return (
    <div className="min-h-screen" style={{ background: '#f5f5f0' }}>
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* ── Hero: identitas halaman + meta snapshot + kontrol refresh ── */}
      <div className="relative text-white mb-5" style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div className="relative flex flex-wrap items-center justify-between gap-4" style={{ padding: '16px 22px' }}>
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="min-w-0">
              <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>AWS CloudTrail</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                {ctAccounts.length > 1 && (
                  <select
                    value={ctAccount}
                    onChange={(e) => switchCtAccount(e.target.value)}
                    className="pl-3 pr-2 py-1 rounded-full bg-white/15 ring-1 ring-inset ring-white/20 text-xs font-semibold text-white outline-none focus:ring-white/40 cursor-pointer"
                    title="AWS account"
                  >
                    {ctAccounts.map((a) => (
                      <option key={a.key} value={a.key} className="text-slate-800">{a.label}</option>
                    ))}
                  </select>
                )}
                {meta?.accountId && (
                  <span className={chip}>
                    <Cloud style={{ width: 13, height: 13 }} className="text-white/60" />
                    <span className="text-white/60">{t('awsTrail.account')}</span>
                    <span className="font-mono">{meta.accountId}</span>
                  </span>
                )}
                {meta?.days != null && (
                  <span className={chip}>
                    <Calendar style={{ width: 13, height: 13 }} className="text-white/60" />
                    {meta.days} {t('awsTrail.days')} · {meta.includeReadOnly ? t('awsTrail.allEvents') : 'write events'}
                  </span>
                )}
                {lastRefreshed && (
                  <span className={chip}>
                    <Clock style={{ width: 13, height: 13 }} className="text-white/60" />
                    {t('awsTrail.updated')}: {lastRefreshed}
                  </span>
                )}
                {meta?.durationMs != null && (
                  <span className={chip} title="Last fetch duration">
                    <Timer style={{ width: 13, height: 13 }} className="text-white/60" />
                    {(meta.durationMs / 1000).toFixed(1)}s
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {/* Window (hari) */}
              <label className={`inline-flex items-center gap-2 pl-3 pr-2 h-9 rounded-xl cursor-pointer ${ctrl}`}>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{t('awsTrail.days2')}</span>
                <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                  className="bg-transparent text-sm font-semibold text-white outline-none cursor-pointer">
                  {[1, 3, 7, 14, 30, 60, 90].map((d) => <option key={d} value={d} className="text-slate-800">{d}</option>)}
                </select>
              </label>

              {/* Include read-only (switch) */}
              <button type="button" role="switch" aria-checked={includeReadOnly}
                onClick={() => setIncludeReadOnly((v) => !v)}
                className={`inline-flex items-center gap-2 px-3 h-9 rounded-xl text-sm text-white/90 ${ctrl}`}>
                <span className={`relative inline-block w-8 h-[18px] rounded-full transition-colors ${includeReadOnly ? 'bg-white' : 'bg-white/25'}`}>
                  <span className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full shadow transition-transform ${includeReadOnly ? 'bg-brand-700' : 'bg-white'} ${includeReadOnly ? 'translate-x-[14px]' : ''}`} />
                </span>
                {t('awsTrail.includeReadOnly')}
              </button>

              {/* Settings (region per akun) */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => (ctSetOpen ? setCtSetOpen(false) : openCtSettings())}
                  className={`w-9 h-9 inline-flex items-center justify-center rounded-xl text-white/90 ${ctrl} ${ctSetOpen ? 'bg-white/20' : ''}`}
                  title="CloudTrail settings"
                >
                  <Settings2 style={{ width: 16, height: 16 }} />
                </button>
                {ctSetOpen && (
                  <div className="absolute top-full right-0 mt-2 z-50 w-[460px] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-auto rounded-2xl border border-slate-200 bg-white shadow-2xl p-5 text-left text-slate-800">
                    <div className="flex items-center gap-2 mb-1">
                      <Settings2 style={{ width: 16, height: 16 }} className="text-white/60" />
                      <div className="font-semibold text-slate-900">CloudTrail settings</div>
                    </div>
                    <div className="text-xs text-slate-500 mb-4">Applies to this AWS account only. Takes effect on the next refresh.</div>
                    {!ctSet ? (
                      <div className="text-sm text-slate-500">Loading...</div>
                    ) : (
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-0.5">Regions to look up</div>
                        <div className="text-xs text-slate-500 mb-2.5">
                          Nothing selected means all regions returned by AWS.
                        </div>
                        <div className="grid grid-cols-2 gap-1 mb-4 p-2 rounded-xl bg-slate-50 ring-1 ring-inset ring-slate-100">
                          {(ctSet.availableRegions || []).map((rg) => (
                            <label key={rg} className="flex items-center gap-2 text-xs text-slate-700 px-1.5 py-1 rounded-md hover:bg-white cursor-pointer">
                              <input type="checkbox" checked={ctSet.regions.includes(rg)} onChange={() => toggleCtRegion(rg)} className="rounded border-slate-300" />
                              <span className="font-mono">{rg}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={saveCtSettings}
                            disabled={ctSetSaving}
                            className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-60"
                          >
                            {ctSetSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setCtSetOpen(false)}
                            className="px-4 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => setCtSet({ ...ctSet, regions: [] })}
                            className="text-xs text-slate-500 hover:text-slate-800 underline ml-auto"
                          >
                            Clear region filter
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button onClick={refresh} disabled={refreshing}
              className="inline-flex items-center justify-center gap-2 px-4 h-9 rounded-xl bg-white hover:bg-brand-50 disabled:opacity-70 text-brand-800 text-sm font-semibold shadow-sm transition-colors">
              <RefreshCw style={{ width: 15, height: 15 }} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? (t('awsTrail.fetching13Min')) : (t('awsTrail.refreshAws'))}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-2.5">
          <AlertTriangle className="text-rose-500 flex-shrink-0" style={{ width: 16, height: 16, marginTop: 2 }} />
          <p className="text-sm text-rose-700 font-medium">{error}</p>
        </div>
      )}
      {!loading && hasData && <FetchHealthBanner meta={meta} summary={summary} id={id} />}
      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 animate-pulse">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-40 rounded-2xl bg-white border border-slate-200/70 p-5">
                <div className="h-3 w-24 bg-slate-100 rounded" />
                <div className="h-7 w-20 bg-slate-100 rounded mt-3" />
                <div className="h-10 bg-slate-50 rounded mt-6" />
              </div>
            ))}
          </div>
          <div className="h-96 rounded-2xl bg-white border border-slate-200/70 flex items-center justify-center gap-3 text-slate-400">
            <RefreshCw className="animate-spin" style={{ width: 18, height: 18 }} />
            <span className="text-sm font-medium">{t('awsTrail.loading')}</span>
          </div>
        </div>
      ) : !hasData ? (
        <div className="bg-white rounded-3xl border border-slate-200/70 shadow-sm text-center py-16 px-6">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-brand-50 ring-1 ring-inset ring-brand-100 flex items-center justify-center mb-4">
            <ScrollText className="text-brand-600" style={{ width: 28, height: 28 }} />
          </div>
          <h3 className="font-display text-lg font-bold text-slate-900 mb-1">{t('awsTrail.noCloudtrailDataYet')}</h3>
          <p className="text-sm text-slate-500 mb-6">{t('awsTrail.clickRefreshFetchActivityEvents')}</p>
          <button onClick={refresh} disabled={refreshing}
            className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-70 text-white text-sm font-semibold shadow-sm transition-colors">
            <RefreshCw style={{ width: 15, height: 15 }} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? (t('awsTrail.fetching13Min')) : (t('awsTrail.refreshAws'))}
          </button>
        </div>
      ) : (
        <>
          <SummaryCards
            summary={summary} meta={meta} locale={locale} trend={trend}
            activeKpi={activeKpi}
            onTotal={resetFilters}
            onFailed={filterErrorsOnly}
            onTop={filterTopEvent}
          />

          {/* Tab: segmented control */}
          <div className="inline-flex flex-wrap gap-1 p-1 bg-slate-200/50 rounded-2xl ring-1 ring-inset ring-slate-200/80 mb-4">
            {TABS.map((tb) => {
              const on = activeTab === tb.key;
              const Ic = tb.icon;
              return (
                <button key={tb.key} onClick={() => setActiveTab(tb.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all ${on ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'}`}>
                  <Ic style={{ width: 15, height: 15 }} className={on ? 'text-brand-600' : ''} />
                  {tb.label}
                  {tb.count != null && (
                    <span className={`text-[11px] tabular-nums px-1.5 py-px rounded-md ${on ? 'bg-slate-100 text-slate-600' : 'text-slate-400'}`}>{tb.count.toLocaleString(locale)}</span>
                  )}
                </button>
              );
            })}
          </div>

          {activeTab === 'useractivity' ? (
            <UserActivity />
          ) : activeTab === 'events' ? (
            <EventTable
              rows={sorted} totalRaw={events.length} failedTotal={failedTotal}
              query={query} setQuery={setQuery}
              sortCol={sortCol} sortDir={sortDir} toggleSort={toggleSort}
              errorsOnly={errorsOnly} setErrorsOnly={setErrorsOnly}
              regionFilter={regionFilter} setRegionFilter={setRegionFilter} regionOptions={regionOptions}
              activeId={selected?.eventId}
              onRowClick={(row) => setSelected((p) => (p && p.eventId === row.eventId ? null : row))}
              locale={locale} id={id}
            />
          ) : activeTab === 'vpnlog' ? (
            <VpnTunnelLog />
          ) : (
            <AnalyticsPanel events={events} meta={meta} locale={locale} id={id}
              onDrill={(title, rows) => setDrill({ title, rows })}
            />
          )}
        </>
      )}
      {selected && (
        <Drawer onClose={() => setSelected(null)}>
          <EventDetail item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />
        </Drawer>
      )}
      {drill && (
        <AnalyticsDrawer title={drill.title} rows={drill.rows} locale={locale} id={id} onClose={() => setDrill(null)} />
      )}
    </div>
    </div>
  );
}

// ── Analytics Panel ───────────────────────────────────────────────────────────
// Analitik aktivitas & keamanan atas snapshot CloudTrail (window meta.days).
// Dihitung penuh di client dari `events` — tanpa endpoint baru. Tanpa library
// chart: bar horizontal murni Tailwind. Paket C: keamanan + aktivitas + anomali.

// Aksi ber-risiko tinggi yang layak diawasi (write/security-sensitive).
const SENSITIVE_EVENTS = new Set([
  'ConsoleLogin', 'CreateUser', 'DeleteUser', 'CreateAccessKey', 'DeleteAccessKey',
  'AttachUserPolicy', 'AttachRolePolicy', 'PutUserPolicy', 'PutRolePolicy',
  'CreateRole', 'DeleteRole', 'UpdateAssumeRolePolicy', 'CreateLoginProfile',
  'UpdateLoginProfile', 'DeactivateMFADevice', 'CreatePolicyVersion',
  'DeleteTrail', 'StopLogging', 'UpdateTrail', 'PutEventSelectors',
  'PutBucketPolicy', 'PutBucketAcl', 'DeleteBucketPolicy',
  'AuthorizeSecurityGroupIngress', 'RevokeSecurityGroupIngress',
  'DisableKey', 'ScheduleKeyDeletion', 'PutKeyPolicy',
  'GetFederationToken', 'DeleteDBInstance', 'ModifyDBInstance',
]);

// Klasifikasi sourceIp: internal (AWS Internal), service principal (domain),
// private (RFC1918), atau publik (IPv4 rutable).
const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
function classifyIp(ip) {
  if (!ip) return 'none';
  if (ip === 'AWS Internal') return 'internal';
  if (!IPV4_RE.test(ip)) return 'service'; // mis. cloudtrail.amazonaws.com
  if (/^10\.|^192\.168\.|^172\.(1[6-9]|2\d|3[01])\./.test(ip)) return 'private';
  return 'public';
}

function AnalyticsPanel({ events, meta, locale, id, onDrill }) {
  const T = (idStr, enStr) => (id ? idStr : enStr);
  const nf = (n) => (n || 0).toLocaleString(locale);
  const isDenied = (e) => /denied|unauthorized|forbidden/i.test(e.errorCode || '');
  // Granularitas dari RENTANG DATA sebenarnya (snapshot bisa menumpuk jauh lebih
  // lama dari meta.days): <= 2 hari per jam, <= 120 hari per hari, selebihnya per minggu.
  const gran = useMemo(() => {
    let lo = null; let hi = null;
    events.forEach((e) => {
      const s = e.eventTime;
      if (!s || !ISO_RE.test(s)) return;
      if (lo === null || s < lo) lo = s;
      if (hi === null || s > hi) hi = s;
    });
    if (!lo) return 'day';
    const spanDays = (new Date(hi) - new Date(lo)) / 86400000;
    return spanDays <= 2 ? 'hour' : spanDays <= 120 ? 'day' : 'week';
  }, [events]);
  const timeKeyOf = (e) => {
    const s = e.eventTime || '';
    if (!ISO_RE.test(s)) return null;
    if (gran === 'hour') return s.slice(0, 13).replace('T', ' ') + ':00';
    if (gran === 'day') return s.slice(0, 10);
    const d = new Date(s.slice(0, 10) + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); // awal minggu (Senin)
    return 'Wk ' + d.toISOString().slice(0, 10);
  };
  // drill(judul, predikat) → buka drawer kanan berisi event yang cocok.
  const drill = (title, fn) => onDrill && onDrill(title, events.filter(fn));

  const A = useMemo(() => {
    const total = events.length;
    const failed = events.filter((e) => e.errorCode);
    const denied = failed.filter((e) => /denied|unauthorized|forbidden/i.test(e.errorCode || ''));

    const tally = (arr, key) => {
      const m = new Map();
      arr.forEach((e) => { const v = key(e); if (v == null || v === '') return; m.set(v, (m.get(v) || 0) + 1); });
      return [...m.entries()].map(([k, v]) => ({ k, v })).sort((a, b) => b.v - a.v);
    };

    // Aktivitas per hari (atau per jam bila window ≤ 2 hari).
    const byHour = gran === 'hour';
    const timeKey = timeKeyOf;
    const timeSeries = tally(events, timeKey).sort((a, b) => (a.k < b.k ? -1 : 1));

    // Anomali: titik waktu dgn count > 1,5× rata-rata.
    const counts = timeSeries.map((x) => x.v);
    const mean = counts.length ? counts.reduce((s, v) => s + v, 0) / counts.length : 0;
    const spikes = timeSeries.filter((x) => mean > 0 && x.v > mean * 1.5);

    // Denial spike per hari.
    const denialSeries = tally(denied, timeKey);
    const denMean = denialSeries.length ? denialSeries.reduce((s, x) => s + x.v, 0) / denialSeries.length : 0;
    const denialSpikes = denialSeries.filter((x) => denMean > 0 && x.v > denMean * 1.5).sort((a, b) => b.v - a.v);

    const topEvents = tally(events, (e) => e.eventName);
    const topSources = tally(events, (e) => (e.eventSource || '').replace('.amazonaws.com', ''));
    const topUsers = tally(events, (e) => e.username);
    const byRegion = tally(events, (e) => e.region);
    const byUserType = tally(events, (e) => e.userType);

    const deniedActions = tally(denied, (e) => e.eventName).slice(0, 8);
    const deniedPrincipals = tally(denied, (e) => e.username).slice(0, 6);

    // Source IP.
    const ipClass = { public: 0, private: 0, internal: 0, service: 0, none: 0 };
    events.forEach((e) => { ipClass[classifyIp(e.sourceIp)]++; });
    const publicIps = tally(events.filter((e) => classifyIp(e.sourceIp) === 'public'), (e) => e.sourceIp).slice(0, 8);
    const publicDenials = new Map();
    denied.forEach((e) => { if (classifyIp(e.sourceIp) === 'public') publicDenials.set(e.sourceIp, (publicDenials.get(e.sourceIp) || 0) + 1); });

    // Sensitive watchlist.
    const sensitive = tally(events.filter((e) => SENSITIVE_EVENTS.has(e.eventName)), (e) => e.eventName)
      .map((x) => {
        const rows = events.filter((e) => e.eventName === x.k);
        const failedN = rows.filter((e) => e.errorCode).length;
        const last = rows.map((e) => e.eventTime).filter(Boolean).sort().pop();
        const users = [...new Set(rows.map((e) => e.username).filter(Boolean))];
        return { ...x, failedN, last, users };
      });

    // Root usage.
    const rootN = events.filter((e) => e.userType === 'Root' || e.username === 'root').length;

    return {
      total, failedN: failed.length, deniedN: denied.length,
      failRate: total ? (failed.length / total) * 100 : 0,
      byHour, timeSeries, spikes, denialSpikes,
      topEvents, topSources, topUsers, byRegion, byUserType,
      deniedActions, deniedPrincipals,
      ipClass, publicIps, publicDenials,
      sensitive, rootN,
      regionsScanned: meta?.regionsScanned || [],
    };
  }, [events, meta, locale, gran]); // eslint-disable-line

  // Bar horizontal: track tipis + nilai + porsi terhadap total event.
  const Bars = ({ items, max, color = 'bg-indigo-500', onClick }) => {
    const m = max || Math.max(1, ...items.map((x) => x.v));
    return (
      <div className="space-y-0.5">
        {items.map((x) => (
          <button key={x.k} type="button" onClick={onClick ? () => onClick(x.k) : undefined}
            className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-lg group transition-colors ${onClick ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'}`}>
            <div className="w-40 flex-shrink-0 text-xs text-slate-600 truncate text-left group-hover:text-slate-900" title={x.k}>{x.k}</div>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${color} rounded-full`} style={{ width: `${Math.max(2, (x.v / m) * 100)}%` }} />
            </div>
            <div className="w-12 flex-shrink-0 text-xs font-semibold text-slate-800 text-right tabular-nums">{nf(x.v)}</div>
            <div className="w-11 flex-shrink-0 text-[11px] text-slate-400 text-right tabular-nums">{A.total ? `${((x.v / A.total) * 100).toFixed(1)}%` : ''}</div>
          </button>
        ))}
      </div>
    );
  };

  const ICON_TILE = {
    blue: 'bg-indigo-50 text-indigo-600 ring-indigo-100',
    rose: 'bg-rose-50 text-rose-600 ring-rose-100',
    amber: 'bg-amber-50 text-amber-600 ring-amber-100',
    emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    slate: 'bg-slate-100 text-slate-500 ring-slate-200',
  };
  const Card = ({ icon: Icon, title, children, tone = 'slate', right }) => (
    <div className="bg-white border border-slate-200/70 rounded-2xl p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg ring-1 ring-inset flex items-center justify-center ${ICON_TILE[tone] || ICON_TILE.slate}`}>
            <Icon style={{ width: 15, height: 15 }} />
          </div>
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </div>
  );

  const STAT = {
    blue: { dot: 'bg-indigo-500', val: 'text-slate-900' },
    amber: { dot: 'bg-amber-500', val: 'text-amber-700' },
    rose: { dot: 'bg-rose-500', val: 'text-rose-700' },
    emerald: { dot: 'bg-emerald-500', val: 'text-emerald-700' },
    slate: { dot: 'bg-slate-400', val: 'text-slate-900' },
  };
  const Stat = ({ label, value, sub, tone = 'slate', onClick }) => {
    const s = STAT[tone] || STAT.slate;
    return (
      <div onClick={onClick} className={`bg-white rounded-2xl p-4 border border-slate-200/70 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all' : ''}`}>
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <span className={`w-2 h-2 rounded-full ${s.dot}`} />{label}
        </div>
        <div className={`mt-1.5 font-display text-2xl font-bold tabular-nums ${s.val}`}>{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </div>
    );
  };

  const tsMax = Math.max(1, ...A.timeSeries.map((d) => d.v));
  const tsAvg = A.timeSeries.length ? A.timeSeries.reduce((s, d) => s + d.v, 0) / A.timeSeries.length : 0;

  return (
    <div className="space-y-4">
      {/* Ringkasan keamanan */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={T('Total event', 'Total events')} value={nf(A.total)} sub={`${meta?.days ?? 7} ${T('hari', 'days')}`} tone="blue"
          onClick={() => drill(T('Semua event', 'All events'), () => true)} />
        <Stat label={T('Gagal / error', 'Failed / errors')} value={nf(A.failedN)} sub={A.failRate.toFixed(1) + '%'} tone="amber"
          onClick={() => drill(T('Event gagal', 'Failed events'), (e) => e.errorCode)} />
        <Stat label={T('Ditolak (denied)', 'Denied')} value={nf(A.deniedN)} tone="rose"
          onClick={() => drill(T('Event ditolak', 'Denied events'), isDenied)} />
        <Stat label={T('Aktivitas root', 'Root activity')} value={nf(A.rootN)} sub={A.rootN > 0 ? T('perlu ditinjau', 'review advised') : T('tidak ada', 'none')} tone={A.rootN > 0 ? 'rose' : 'emerald'}
          onClick={A.rootN > 0 ? () => drill(T('Aktivitas root', 'Root activity'), (e) => e.userType === 'Root' || e.username === 'root') : undefined} />
      </div>

      {/* Aktivitas dari waktu ke waktu */}
      <Card icon={Activity} title={gran === 'hour' ? T('Aktivitas per jam', 'Activity per hour') : gran === 'day' ? T('Aktivitas per hari', 'Activity per day') : T('Aktivitas per minggu', 'Activity per week')} tone="blue"
        right={
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-500" />Normal</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />{T('Lonjakan', 'Spike')}</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3.5 border-t-2 border-dashed border-slate-400" />{T('Rata-rata', 'Average')}</span>
          </div>
        }>
        {A.timeSeries.length ? (
          <div className="flex gap-3">
            <div className="flex flex-col justify-between h-44 text-[10px] text-slate-400 tabular-nums text-right w-9 flex-shrink-0 -mt-1.5 -mb-1.5">
              <span>{nf(tsMax)}</span><span>{nf(Math.round(tsMax / 2))}</span><span>0</span>
            </div>
            <div className="relative flex-1 min-w-0 h-44 overflow-hidden">
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {[0, 1, 2].map((i) => <div key={i} className="border-t border-slate-100" />)}
              </div>
              <div className="absolute left-0 right-0 border-t-2 border-dashed border-slate-400/60 pointer-events-none z-10"
                style={{ bottom: `${(tsAvg / tsMax) * 100}%` }} title={`${T('Rata-rata', 'Average')}: ${nf(Math.round(tsAvg))}`} />
              <div className={`relative flex items-end h-full ${A.timeSeries.length > 90 ? 'gap-px' : A.timeSeries.length > 40 ? 'gap-0.5' : 'gap-[3px]'}`}>
                {A.timeSeries.map((x) => {
                  const spike = A.spikes.some((s) => s.k === x.k);
                  return (
                    <button key={x.k} type="button" onClick={() => drill(x.k, (e) => timeKeyOf(e) === x.k)}
                      className="flex-1 min-w-0 h-full flex flex-col justify-end group cursor-pointer" title={`${x.k}: ${nf(x.v)}`}>
                      <div className={`w-full rounded-t-[4px] transition-colors ${spike ? 'bg-rose-500 group-hover:bg-rose-600' : 'bg-indigo-500/85 group-hover:bg-indigo-600'}`}
                        style={{ height: `${Math.max(1, (x.v / tsMax) * 100)}%` }} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : <div className="text-sm text-slate-400 py-6 text-center">{T('Tidak ada data waktu.', 'No time data.')}</div>}
        <div className="flex justify-between text-[10px] text-slate-400 mt-2 pl-12">
          <span>{A.timeSeries[0]?.k}</span>
          {A.timeSeries.length > 4 && <span>{A.timeSeries[Math.floor(A.timeSeries.length / 2)]?.k}</span>}
          <span>{A.timeSeries[A.timeSeries.length - 1]?.k}</span>
        </div>
        {A.spikes.length > 0 && (
          <div className="mt-3 text-xs text-rose-700 bg-rose-50 ring-1 ring-inset ring-rose-100 rounded-lg px-3 py-2 flex items-start gap-1.5">
            <AlertTriangle style={{ width: 13, height: 13, marginTop: 1 }} className="flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-semibold">{A.spikes.length} {T('lonjakan', A.spikes.length === 1 ? 'spike' : 'spikes')}</span> {T('(>1,5× rata-rata) — tertinggi:', '(>1.5× average) — highest:')}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {[...A.spikes].sort((a, b) => b.v - a.v).slice(0, 6).map((sp) => (
                  <button key={sp.k} type="button" onClick={() => drill(sp.k, (e) => timeKeyOf(e) === sp.k)}
                    className="px-1.5 py-0.5 rounded-md bg-white ring-1 ring-inset ring-rose-200 hover:bg-rose-100 text-rose-700 tabular-nums">
                    {sp.k} · {nf(sp.v)}
                  </button>
                ))}
                {A.spikes.length > 6 && <span className="px-1.5 py-0.5 text-rose-500">+{A.spikes.length - 6} {T('lainnya', 'more')}</span>}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Grid keamanan */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={ShieldAlert} title={T('Aksi paling sering ditolak', 'Most-denied actions')} tone="rose">
          {A.deniedActions.length ? <Bars items={A.deniedActions} color="bg-rose-500" onClick={(k) => drill(k, (e) => e.eventName === k && isDenied(e))} />
            : <div className="text-sm text-emerald-600 flex items-center gap-1.5"><CheckCircle2 style={{ width: 14, height: 14 }} /> {T('Tidak ada penolakan.', 'No denials.')}</div>}
          {A.deniedPrincipals.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="text-[11px] font-semibold text-slate-400 uppercase mb-2">{T('Principal dgn denial terbanyak', 'Top denied principals')}</div>
              <Bars items={A.deniedPrincipals} color="bg-rose-400" onClick={(k) => drill(k, (e) => e.username === k && isDenied(e))} />
            </div>
          )}
        </Card>

        <Card icon={Lock} title={T('Watchlist aksi sensitif', 'Sensitive-action watchlist')} tone="amber">
          {A.sensitive.length ? (
            <div className="space-y-2">
              {A.sensitive.slice(0, 10).map((x) => (
                <button key={x.k} type="button" onClick={() => drill(x.k, (e) => e.eventName === x.k)}
                  className="w-full flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0 text-left hover:bg-slate-50 rounded px-1 -mx-1">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700 truncate">{x.k}</div>
                    <div className="text-[11px] text-slate-400 truncate">{x.users.slice(0, 3).join(', ') || '—'}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {x.failedN > 0 && <span className="text-[10px] font-semibold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">{x.failedN} {T('gagal', 'failed')}</span>}
                    <span className="text-sm font-bold text-slate-700 tabular-nums">{nf(x.v)}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : <div className="text-sm text-emerald-600 flex items-center gap-1.5"><ShieldCheck style={{ width: 14, height: 14 }} /> {T('Tidak ada aksi sensitif terdeteksi.', 'No sensitive actions detected.')}</div>}
        </Card>

        <Card icon={Globe2} title={T('Analisis Source IP', 'Source IP analysis')} tone="blue">
          <div className="grid grid-cols-4 gap-2 mb-4 text-center">
            {[
              { k: 'public', lbl: T('Publik', 'Public'), box: 'bg-rose-50', val: 'text-rose-700', sub: 'text-rose-500' },
              { k: 'private', lbl: T('Privat', 'Private'), box: 'bg-slate-50', val: 'text-slate-700', sub: 'text-slate-500' },
              { k: 'internal', lbl: 'AWS', box: 'bg-slate-50', val: 'text-slate-700', sub: 'text-slate-500' },
              { k: 'service', lbl: T('Service', 'Service'), box: 'bg-slate-50', val: 'text-slate-700', sub: 'text-slate-500' },
            ].map((c) => (
              <div key={c.k} className={`rounded-lg py-2 ${c.box}`}>
                <div className={`text-lg font-bold tabular-nums ${c.val}`}>{nf(A.ipClass[c.k])}</div>
                <div className={`text-[10px] ${c.sub}`}>{c.lbl}</div>
              </div>
            ))}
          </div>
          {A.publicIps.length ? (
            <>
              <div className="text-[11px] font-semibold text-slate-400 uppercase mb-2">{T('Top IP publik', 'Top public IPs')}</div>
              <div className="space-y-1.5">
                {A.publicIps.map((x) => {
                  const den = A.publicDenials.get(x.k) || 0;
                  return (
                    <button key={x.k} type="button" onClick={() => drill(x.k, (e) => e.sourceIp === x.k)}
                      className="w-full flex items-center justify-between text-xs hover:bg-slate-50 rounded px-1 -mx-1 py-0.5">
                      <span className="font-mono text-slate-600">{x.k}</span>
                      <span className="flex items-center gap-2">
                        {den > 0 && <span className="text-rose-600 font-semibold">{den} {T('ditolak', 'denied')}</span>}
                        <span className="font-semibold text-slate-700 tabular-nums">{nf(x.v)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : <div className="text-sm text-slate-400">{T('Tidak ada IP publik.', 'No public IPs.')}</div>}
        </Card>

        <Card icon={User} title={T('Principal & tipe user', 'Principals & user types')} tone="blue">
          <div className="text-[11px] font-semibold text-slate-400 uppercase mb-2">{T('User paling aktif', 'Most active users')}</div>
          <Bars items={A.topUsers.slice(0, 6)} color="bg-indigo-500" onClick={(k) => drill(k, (e) => e.username === k)} />
          {A.byUserType.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="text-[11px] font-semibold text-slate-400 uppercase mb-2">{T('Tipe user', 'User types')}</div>
              <div className="flex flex-wrap gap-2">
                {A.byUserType.map((x) => (
                  <button key={x.k} type="button" onClick={() => drill(x.k, (e) => e.userType === x.k)}
                    className={`text-xs px-2 py-1 rounded-lg hover:ring-2 hover:ring-offset-1 hover:ring-slate-200 ${/root/i.test(x.k) ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`}>{x.k}: <strong>{nf(x.v)}</strong></button>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Aktivitas: top aksi / service / region */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={Zap} title={T('Aksi (event) terbanyak', 'Top actions (events)')} tone="blue">
          <Bars items={A.topEvents.slice(0, 10)} color="bg-indigo-500" onClick={(k) => drill(k, (e) => e.eventName === k)} />
        </Card>
        <Card icon={Cloud} title={T('Service terbanyak', 'Top services')} tone="blue">
          <Bars items={A.topSources.slice(0, 10)} color="bg-sky-500" onClick={(k) => drill(k, (e) => (e.eventSource || '').replace('.amazonaws.com', '') === k)} />
        </Card>
      </div>

      <Card icon={MapPin} title={T('Aktivitas per region', 'Activity per region')} tone="blue">
        {A.byRegion.length ? <Bars items={A.byRegion} color="bg-emerald-500" onClick={(k) => drill(k, (e) => e.region === k)} /> : <div className="text-sm text-slate-400">{T('Tidak ada data region.', 'No region data.')}</div>}
        {A.regionsScanned.length > 0 && (
          <div className="mt-3 text-[11px] text-slate-400">
            {T('Region dipindai', 'Regions scanned')}: {A.regionsScanned.length} · {T('dengan event', 'with events')}: {A.byRegion.length}
          </div>
        )}
      </Card>

      {/* Anomali denial */}
      {A.denialSpikes.length > 0 && (
        <Card icon={AlertTriangle} title={T('Lonjakan penolakan (anomali)', 'Denial spikes (anomaly)')} tone="rose">
          <div className="space-y-2">
            {A.denialSpikes.slice(0, 8).map((s) => (
              <button key={s.k} type="button" onClick={() => drill(s.k, (e) => isDenied(e) && timeKeyOf(e) === s.k)}
                className="w-full flex items-center justify-between bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 hover:bg-rose-100/70 text-left">
                <span className="text-sm font-medium text-slate-700">{s.k}</span>
                <span className="text-sm font-bold text-rose-600 tabular-nums">{nf(s.v)} {T('penolakan', 'denials')}</span>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}


// ── Analytics Drawer ──────────────────────────────────────────────────────────
// Drawer kanan untuk drill-through dari Analytics: menampilkan daftar event di
// balik angka yang diklik; klik satu baris → detail JSON penuh (EventDetail).
function AnalyticsDrawer({ title, rows, locale, id, onClose }) {
  const {
    t
  } = useLang();

  const [picked, setPicked] = useState(null);
  return (
    <Drawer onClose={onClose}>
      {picked ? (
        <div>
          <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur px-5 py-3 border-b border-slate-200">
            <button onClick={() => setPicked(null)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700">
              <ChevronRight style={{ width: 15, height: 15, transform: 'rotate(180deg)' }} />
              {t('awsTrail.backList')} ({rows.length})
            </button>
          </div>
          <EventDetail item={picked} onClose={onClose} locale={locale} id={id} />
        </div>
      ) : (
        <div>
          <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{t('awsTrail.eventDetail')}</div>
              <h2 className="font-display text-lg font-bold text-slate-800 truncate" title={title}>{title}</h2>
              <div className="text-sm text-slate-500">{rows.length.toLocaleString(locale)} {t('awsTrail.events')}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0"><X style={{ width: 20, height: 20 }} /></button>
          </div>
          <div className="p-3">
            {rows.length ? rows.map((r, i) => {
              const failed = !!r.errorCode;
              return (
                <button key={r.eventId || i} type="button" onClick={() => setPicked(r)}
                  className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-100 transition-all mb-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                      {failed && <XCircle className="text-rose-500 flex-shrink-0" style={{ width: 13, height: 13 }} />}
                      {r.eventName || '—'}
                    </span>
                    <span className="text-[11px] text-slate-400 flex-shrink-0">{fmtCell(r.eventTime, locale)}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {(r.username || '—')} · {(r.eventSource || '—').replace('.amazonaws.com', '')} · {r.region || '—'}
                    {r.sourceIp ? <span className="font-mono"> · {r.sourceIp}</span> : null}
                    {failed && <span className="text-rose-600 font-semibold"> · {r.errorCode}</span>}
                  </div>
                </button>
              );
            }) : <div className="text-sm text-slate-400 text-center py-10">{t('awsTrail.noEvents')}</div>}
          </div>
        </div>
      )}
    </Drawer>
  );
}

function FetchHealthBanner({ meta, summary, id }) {
  const {
    t
  } = useLang();

  const [open, setOpen] = useState(false);
  const errors = meta?.errors || [];
  const scanned = meta?.regionsScanned || [];
  const byRegion = summary?.byRegion || {};
  // Region yang discan tapi 0 event (kandidat terpotong/gagal diam-diam).
  const emptyRegions = scanned.filter((r) => !byRegion[r]);
  const hasIssue = errors.length > 0;

  if (!hasIssue) return null;

  return (
    <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-start gap-2.5 px-4 py-3 text-left">
        <AlertTriangle className="text-amber-500 flex-shrink-0" style={{ width: 16, height: 16, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">
            {t('awsTrail.lengthRegionSFailedFetch', {
              length: errors.length
            })}
          </p>
          <p className="text-xs text-amber-600 mt-0.5">
            {t('awsTrail.oftenCausedCloudtrailApiThrottling')}
          </p>
        </div>
        <ChevronDown style={{ width: 15, height: 15 }} className={`text-amber-500 flex-shrink-0 mt-0.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 border-t border-amber-200/60">
          <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1.5">{t('awsTrail.failedRegions')}</div>
          <div className="space-y-1 mb-3">
            {errors.map((e, i) => (
              <div key={i} className="text-[12px] font-mono text-amber-800 bg-amber-100/60 rounded px-2 py-1 break-words">{e}</div>
            ))}
          </div>
          {emptyRegions.length > 0 && (
            <>
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1.5">{t('awsTrail.regionsNoEvents')}</div>
              <div className="flex flex-wrap gap-1">
                {emptyRegions.map((r) => (
                  <span key={r} className="text-[11px] font-medium text-amber-700 bg-amber-100/60 rounded px-1.5 py-0.5">{r}</span>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sparkline (SVG murni, tanpa library chart) ────────────────────────────────
function Sparkline({ data, color, height = 40 }) {
  const gid = useMemo(() => 'spk' + Math.random().toString(36).slice(2, 9), []);
  if (!data || data.length < 2) return <div style={{ height }} />;
  const w = 200; const h = height;
  const max = Math.max(1, ...data);
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => [i * step, h - 3 - (v / max) * (h - 6)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full block" style={{ height }} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ── Kartu KPI ─────────────────────────────────────────────────────────────────
const KPI_TONES = {
  indigo:  { tile: 'bg-indigo-50 text-indigo-600 ring-indigo-100',    ring: 'ring-indigo-300' },
  rose:    { tile: 'bg-rose-50 text-rose-600 ring-rose-100',          ring: 'ring-rose-300' },
  emerald: { tile: 'bg-emerald-50 text-emerald-600 ring-emerald-100', ring: 'ring-emerald-300' },
  amber:   { tile: 'bg-amber-50 text-amber-600 ring-amber-100',       ring: 'ring-amber-300' },
  slate:   { tile: 'bg-slate-100 text-slate-500 ring-slate-200',      ring: 'ring-slate-300' },
};
function KpiCard({ icon: Icon, label, value, sub, tone = 'slate', title, onClick, active, disabled, children }) {
  const c = KPI_TONES[tone] || KPI_TONES.slate;
  const clickable = !!onClick && !disabled;
  return (
    <div
      title={title || undefined}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      className={`group bg-white rounded-2xl border p-5 shadow-sm transition-all flex flex-col text-left
        ${clickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}
        ${active ? `border-transparent ring-2 ${c.ring}` : 'border-slate-200/70'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="mt-1.5 font-display text-[28px] leading-tight font-bold text-slate-900 tabular-nums truncate">{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-xl ring-1 ring-inset flex items-center justify-center flex-shrink-0 ${c.tile}`}>
          <Icon style={{ width: 18, height: 18 }} />
        </div>
      </div>
      <div className="mt-3 flex-1 flex flex-col justify-end">{children}</div>
      {sub && (
        <div className="mt-2.5 text-xs text-slate-500 truncate flex items-center gap-1">
          <span className="truncate">{sub}</span>
          {clickable && <ChevronRight className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ width: 12, height: 12 }} />}
        </div>
      )}
    </div>
  );
}

// ── Kartu ringkasan ───────────────────────────────────────────────────────────
function SummaryCards({ summary, meta, locale, trend, activeKpi, onTotal, onFailed, onTop }) {
  const {
    t
  } = useLang();
  const nf = (n) => (n || 0).toLocaleString(locale);

  const total   = summary.totalEvents || 0;
  const errors  = summary.errorCount || 0;
  const byRegion = summary.byRegion || {};
  const scanned = meta?.regionsScanned || Object.keys(byRegion);
  const regions = scanned.length;
  const top     = (summary.topEventNames || [])[0];

  // Breakdown event per region untuk tooltip kartu "Regions Scanned".
  const regionTip = scanned.map((r) => `${r}: ${byRegion[r] || 0}`).join('\n');
  const regionRank = Object.entries(byRegion).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const failRate = total ? (errors / total) * 100 : 0;
  const topShare = top && total ? (top.count / total) * 100 : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
      <KpiCard icon={Activity} tone="indigo" label={t('awsTrail.totalEvents')} value={nf(total)}
        onClick={onTotal} active={activeKpi === 'total'} sub={t('awsTrail.clickResetFilters')}>
        <Sparkline data={trend?.all} color="#6366f1" />
      </KpiCard>

      <KpiCard icon={ShieldAlert} tone={errors > 0 ? 'rose' : 'slate'} label={t('awsTrail.failedEvents')} value={nf(errors)}
        onClick={errors > 0 ? onFailed : undefined} disabled={errors === 0} active={activeKpi === 'failed'}
        sub={t('awsTrail.clickViewFailures')}>
        <div className="flex items-baseline justify-between text-xs mb-1.5">
          <span className="text-slate-500">Failure rate</span>
          <span className={`font-semibold tabular-nums ${failRate >= 5 ? 'text-rose-600' : 'text-slate-700'}`}>{failRate.toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-rose-400 to-rose-600" style={{ width: `${Math.min(100, errors ? Math.max(failRate, 2) : 0)}%` }} />
        </div>
      </KpiCard>

      <KpiCard icon={Globe2} tone="emerald" label={t('awsTrail.regionsScanned')} value={regions}
        title={regionTip} sub={t('awsTrail.hoverBreakdown')}>
        <div className="text-xs text-slate-500 mb-1.5">
          <span className="font-semibold text-slate-700 tabular-nums">{regionRank.length}</span> with activity
        </div>
        <div className="flex flex-wrap gap-1">
          {regionRank.slice(0, 3).map(([r, v]) => (
            <span key={r} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 ring-1 ring-inset ring-emerald-100 text-emerald-800 text-[11px] font-medium">
              {r}<span className="text-emerald-600 tabular-nums">{nf(v)}</span>
            </span>
          ))}
        </div>
      </KpiCard>

      <KpiCard icon={Zap} tone="amber" label={t('awsTrail.topEvent')} value={top ? nf(top.count) : '—'}
        onClick={top ? () => onTop(top.name) : undefined} disabled={!top} active={activeKpi === 'top'}
        sub={top ? `${topShare.toFixed(1)}% of all events` : ''}>
        {top && (
          <div className="self-start max-w-full px-2 py-1 rounded-lg bg-amber-50 ring-1 ring-inset ring-amber-100 text-amber-900 text-xs font-mono font-semibold truncate" title={top.name}>
            {top.name}
          </div>
        )}
      </KpiCard>
    </div>
  );
}

// ── Helper tampilan baris event ───────────────────────────────────────────────
function relTime(iso) {
  const d = new Date(iso);
  if (!iso || isNaN(d)) return '';
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
const AVATAR_TONES = [
  'bg-indigo-100 text-indigo-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-800',
  'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700', 'bg-rose-100 text-rose-700', 'bg-teal-100 text-teal-700',
];
function hashStr(s) { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return Math.abs(h); }
function initials(name) {
  const s = String(name || '').replace(/@.*/, '').replace(/^.*[/:]/, '');
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts[1] ? parts[1][0] : (parts[0][1] || ''))).toUpperCase();
}
function UserAvatar({ name }) {
  if (!name) return <span className="w-6 h-6 rounded-full bg-slate-100 flex-shrink-0" />;
  return (
    <span className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${AVATAR_TONES[hashStr(name) % AVATAR_TONES.length]}`}>
      {initials(name)}
    </span>
  );
}

// ── Tabel event ───────────────────────────────────────────────────────────────
function EventTable({
  rows, totalRaw, failedTotal, query, setQuery, sortCol, sortDir, toggleSort,
  errorsOnly, setErrorsOnly, regionFilter, setRegionFilter, regionOptions,
  onRowClick, activeId, locale, id,
}) {
  const {
    t
  } = useLang();

  const colCount = COLUMNS.length + 2;
  const nf = (n) => (n || 0).toLocaleString(locale);

  // ── Pagination: 50 baris per halaman (render ringan) ──
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // Reset ke halaman 1 saat hasil/filter/sort berubah.
  useEffect(() => { setPage(1); }, [query, errorsOnly, regionFilter, sortCol, sortDir, rows.length]);
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(startIdx, startIdx + PAGE_SIZE);

  const filtersOn = !!query || errorsOnly || regionFilter !== '__all__';
  const fieldCls = 'h-9 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 focus:border-slate-400 focus:ring-4 focus:ring-slate-100 outline-none transition';
  const pgBtn = 'w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors';

  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-brand-600 text-white flex items-center justify-center flex-shrink-0">
            <ScrollText style={{ width: 16, height: 16 }} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-slate-900 leading-tight">Event Log</h3>
            <p className="text-xs text-slate-500 tabular-nums">
              {rows.length !== totalRaw ? `${nf(rows.length)} of ${nf(totalRaw)} events match` : `${nf(totalRaw)} events`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Status: All | Failed */}
          <div className="inline-flex p-0.5 rounded-xl bg-slate-100 ring-1 ring-inset ring-slate-200/70">
            <button onClick={() => setErrorsOnly(false)}
              className={`px-3 h-8 text-xs font-semibold rounded-[10px] transition ${!errorsOnly ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              All
            </button>
            <button onClick={() => setErrorsOnly(true)} disabled={!failedTotal && !errorsOnly}
              className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold rounded-[10px] transition disabled:opacity-40 disabled:cursor-not-allowed ${errorsOnly ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
              {t('awsTrail.errorsOnly')}
              <span className="tabular-nums text-slate-400 font-medium">{nf(failedTotal)}</span>
            </button>
          </div>
          {/* Filter region */}
          <div className="relative">
            <MapPin style={{ width: 14, height: 14 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}
              className={`${fieldCls} pl-8 pr-8 appearance-none cursor-pointer`}>
              <option value="__all__">{t('awsTrail.allRegions')}</option>
              {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <ChevronDown style={{ width: 14, height: 14 }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          {/* Search */}
          <div className="relative">
            <Search style={{ width: 14, height: 14 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('awsTrail.search')}
              className={`${fieldCls} pl-9 pr-8 w-64`} />
            {query && (
              <button onClick={() => setQuery('')} title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center">
                <X style={{ width: 12, height: 12 }} />
              </button>
            )}
          </div>
          {filtersOn && (
            <button onClick={() => { setQuery(''); setErrorsOnly(false); setRegionFilter('__all__'); }}
              className="h-9 px-3 text-xs font-semibold text-slate-500 hover:text-slate-900 rounded-xl hover:bg-slate-50 transition-colors">
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
              <th className="w-1 pl-5 pr-0 py-3" />
              {COLUMNS.map((c) => {
                const isActive = sortCol === c;
                return (
                  <th key={c} onClick={() => toggleSort(c)}
                    className={`text-left font-semibold px-4 py-3 whitespace-nowrap cursor-pointer select-none transition-colors ${isActive ? 'text-slate-900' : 'hover:text-slate-800'}`}>
                    <span className="inline-flex items-center gap-1">
                      {FIELD_LABELS[c] || c}
                      {isActive
                        ? <ChevronDown style={{ width: 12, height: 12 }} className={`transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                        : <ChevronsUpDown style={{ width: 11, height: 11 }} className="text-slate-300" />}
                    </span>
                  </th>
                );
              })}
              <th className="pr-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={colCount} className="py-16 text-center">
                <div className="inline-flex flex-col items-center gap-2 text-slate-400">
                  <Search style={{ width: 22, height: 22 }} />
                  <span className="text-sm">{t('awsTrail.noResults')}</span>
                </div>
              </td></tr>
            ) : pageRows.map((r, i) => {
              const isActive = activeId != null && r.eventId === activeId;
              const failed = !!r.errorCode;
              const isPublic = classifyIp(r.sourceIp) === 'public';
              return (
                <tr key={r.eventId || (startIdx + i)} onClick={() => onRowClick(r)}
                  className={`group cursor-pointer transition-colors ${isActive ? 'bg-brand-50/70' : 'hover:bg-slate-50/80'}`}>
                  <td className="pl-5 pr-0 py-3">
                    <span title={failed ? r.errorCode : 'Success'}
                      className={`block w-2 h-2 rounded-full ${failed ? 'bg-rose-500 ring-4 ring-rose-100' : 'bg-emerald-500 ring-4 ring-emerald-50'}`} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-slate-800 tabular-nums">{fmtCell(r.eventTime, locale)}</div>
                    <div className="text-[11px] text-slate-400">{relTime(r.eventTime)}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-semibold text-slate-900">{r.eventName || '—'}</div>
                    {failed && <div className="text-[11px] font-medium text-rose-600 truncate max-w-[240px]" title={r.errorCode}>{r.errorCode}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs font-medium">
                      {(r.eventSource || '—').replace('.amazonaws.com', '')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap max-w-[240px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <UserAvatar name={r.username} />
                      <span className="truncate text-slate-700" title={r.username || ''}>{r.username || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Globe2 style={{ width: 12, height: 12 }} className="text-slate-400" />{r.region || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-mono text-[12px] text-slate-600">{r.sourceIp || '—'}</span>
                    {isPublic && <span className="ml-1.5 px-1.5 py-px rounded text-[10px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">PUBLIC</span>}
                  </td>
                  <td className="pr-5 py-3 text-right">
                    <ChevronRight style={{ width: 15, height: 15 }}
                      className={`inline transition-transform ${isActive ? 'text-brand-600 translate-x-0.5' : 'text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5'}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer: info + pagination */}
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 bg-slate-50/50 text-sm">
          <span className="text-slate-500">
            Showing <b className="text-slate-800 tabular-nums font-semibold">{nf(startIdx + 1)}–{nf(Math.min(startIdx + PAGE_SIZE, rows.length))}</b> of <b className="text-slate-800 tabular-nums font-semibold">{nf(rows.length)}</b>
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={safePage <= 1} className={pgBtn} title="First page">«</button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className={pgBtn} title="Previous page">‹</button>
              <span className="px-3 text-slate-600 font-medium tabular-nums">Page {safePage} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className={pgBtn} title="Next page">›</button>
              <button onClick={() => setPage(totalPages)} disabled={safePage >= totalPages} className={pgBtn} title="Last page">»</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function Drawer({ children, onClose }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setShown(true));
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => { cancelAnimationFrame(r); window.removeEventListener('keydown', onEsc); };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className={`absolute inset-0 bg-slate-900/30 transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <div className={`relative h-full w-full max-w-2xl bg-slate-50 shadow-2xl overflow-y-auto transition-transform duration-200 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}>
        {children}
      </div>
    </div>
  );
}

// ── JSON tree: 1 node (rekursif, collapsible, syntax-highlight) ───────────────
// importantKeys → key yang disorot (mis. errorCode/errorMessage) diberi latar merah.
function JsonNode({ keyName, value, depth, defaultOpen, importantKeys, isLast }) {
  const isObj = value !== null && typeof value === 'object';
  const isArr = Array.isArray(value);
  const [open, setOpen] = useState(depth < (defaultOpen ?? 1));

  const important = keyName && importantKeys?.has(keyName);
  const keyEl = keyName != null && (
    <span className={`font-semibold ${important ? 'text-rose-600 bg-rose-50 px-1 rounded' : 'text-slate-700'}`}>
      "{keyName}"
    </span>
  );
  const colon = keyName != null ? <span className="text-slate-400">: </span> : null;
  const comma = !isLast ? <span className="text-slate-300">,</span> : null;
  const indent = { paddingLeft: depth * 14 };

  // Primitive → satu baris berwarna.
  if (!isObj) {
    let valEl;
    if (value === null)            valEl = <span className="text-rose-500">null</span>;
    else if (typeof value === 'boolean') valEl = <span className="text-amber-600">{String(value)}</span>;
    else if (typeof value === 'number')  valEl = <span className="text-brand-600">{value}</span>;
    else                            valEl = <span className={`${important ? 'text-rose-700' : 'text-emerald-700'} break-all`}>"{String(value)}"</span>;
    return (
      <div style={indent} className="leading-relaxed">
        {keyEl}{colon}{valEl}{comma}
      </div>
    );
  }

  // Object/Array → header bisa di-collapse.
  const entries = isArr ? value.map((v, i) => [i, v]) : Object.entries(value);
  const openBracket = isArr ? '[' : '{';
  const closeBracket = isArr ? ']' : '}';
  const count = entries.length;

  return (
    <div style={indent} className="leading-relaxed">
      <span className="cursor-pointer select-none inline-flex items-center hover:bg-slate-100 rounded px-0.5 -ml-0.5"
        onClick={() => setOpen((o) => !o)}>
        <ChevronRight style={{ width: 11, height: 11 }}
          className={`text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} />
        {keyEl}{keyName != null && colon}
        <span className="text-slate-400">{openBracket}</span>
        {!open && (
          <span className="text-slate-400">
            <span className="text-slate-300 text-[10px] mx-1">{count} {count === 1 ? 'item' : 'items'}</span>
            {closeBracket}{comma}
          </span>
        )}
      </span>
      {open && (
        <>
          {entries.map(([k, v], i) => (
            <JsonNode
              key={k}
              keyName={isArr ? null : k}
              value={v}
              depth={depth + 1}
              defaultOpen={defaultOpen}
              importantKeys={importantKeys}
              isLast={i === entries.length - 1}
            />
          ))}
          <div style={{ paddingLeft: depth * 14 }}>
            <span className="text-slate-400">{closeBracket}</span>{comma}
          </div>
        </>
      )}
    </div>
  );
}

// ── JSON viewer: toggle Tree/Raw + Copy + Expand/Collapse-all + collapsible card ──
function JsonViewer({ data, id, importantKeys, defaultCollapsed }) {
  const {
    t
  } = useLang();

  const [mode, setMode]   = useState('tree');   // 'tree' | 'raw'
  const [open, setOpen]   = useState(!defaultCollapsed); // card collapse
  const [copied, setCopied] = useState(false);
  const [allKey, setAllKey] = useState(0);      // remount tree untuk expand/collapse-all
  const [allDepth, setAllDepth] = useState(2);  // defaultOpen depth saat remount

  const text = useMemo(() => JSON.stringify(data, null, 2), [data]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard tidak tersedia */ }
  };

  const expandAll   = () => { setAllDepth(99); setAllKey((k) => k + 1); };
  const collapseAll = () => { setAllDepth(1);  setAllKey((k) => k + 1); };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100">
        <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide hover:text-slate-700 transition-colors">
          <ChevronDown style={{ width: 13, height: 13 }} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
          <ScrollText style={{ width: 13, height: 13 }} /> {t('awsTrail.jsonDetail')}
        </button>
        {open && (
          <div className="flex items-center gap-1.5">
            {mode === 'tree' && (
              <>
                <button onClick={expandAll} title={t('awsTrail.expandAll')}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                  <Maximize2 style={{ width: 13, height: 13 }} />
                </button>
                <button onClick={collapseAll} title={t('awsTrail.collapseAll')}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                  <Minimize2 style={{ width: 13, height: 13 }} />
                </button>
                <span className="w-px h-4 bg-slate-200 mx-0.5" />
              </>
            )}
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
              <button onClick={() => setMode('tree')}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${mode === 'tree' ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                Tree
              </button>
              <button onClick={() => setMode('raw')}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${mode === 'raw' ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                Raw
              </button>
            </div>
            <button onClick={copy} title={t('awsTrail.copyJson')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors
                ${copied ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-white'}`}>
              {copied ? <Check style={{ width: 13, height: 13 }} /> : <Copy style={{ width: 13, height: 13 }} />}
              {copied ? (t('awsTrail.copied')) : (t('awsTrail.copy'))}
            </button>
          </div>
        )}
      </div>
      {open && (
        <div className="p-3">
          {mode === 'tree' ? (
            <div className="text-[12px] font-mono bg-slate-50 rounded-lg p-3 overflow-x-auto">
              <JsonNode key={allKey} value={data} depth={0} defaultOpen={allDepth} importantKeys={importantKeys} isLast />
            </div>
          ) : (
            <pre className="text-[11px] font-mono text-slate-600 bg-slate-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
{text}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ── Badge kecil berwarna ──────────────────────────────────────────────────────
function Badge({ icon: Icon, text, tone }) {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red:   'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue:  'bg-brand-50 text-brand-700 border-brand-200',
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${tones[tone] || tones.slate}`}>
      {Icon && <Icon style={{ width: 12, height: 12 }} />}{text}
    </span>
  );
}

// ── Section kartu (Who / What / Where / Result) ───────────────────────────────
function Section({ icon: Icon, title, tone, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
      <div className={`flex items-center gap-2 mb-2.5 ${tone || 'text-slate-400'}`}>
        <Icon style={{ width: 15, height: 15 }} />
        <span className="text-xs font-semibold uppercase tracking-wide">{title}</span>
      </div>
      {children}
    </div>
  );
}

// Bangun kalimat narasi dari field event.
function buildNarrative(item, locale, id, t) {
  const who = item.username || (t('awsTrail.unknownUser'));
  const action = item.eventName || (t('awsTrail.action'));
  const svc = (item.eventSource || '').replace('.amazonaws.com', '');
  const where = item.region || '—';
  const when = fmtCell(item.eventTime, locale);
  const failed = !!item.errorCode;

  if (id) {
    return failed
      ? <>
          <span className="font-semibold text-slate-800">{who}</span> mencoba <span className="font-semibold text-slate-800">{action}</span>
          {svc && <> pada layanan <span className="font-medium">{svc}</span></>} di <span className="font-medium">{where}</span> ({when}) — namun <span className="font-semibold text-rose-600">ditolak</span> ({item.errorCode}).
        </>
      : <>
          <span className="font-semibold text-slate-800">{who}</span> menjalankan <span className="font-semibold text-slate-800">{action}</span>
          {svc && <> pada layanan <span className="font-medium">{svc}</span></>} di <span className="font-medium">{where}</span> ({when}) — <span className="font-semibold text-emerald-600">berhasil</span>.
        </>;
  }
  return failed
    ? <>
        <span className="font-semibold text-slate-800">{who}</span> attempted <span className="font-semibold text-slate-800">{action}</span>
        {svc && <> on <span className="font-medium">{svc}</span></>} in <span className="font-medium">{where}</span> ({when}) — but it was <span className="font-semibold text-rose-600">denied</span> ({item.errorCode}).
      </>
    : <>
        <span className="font-semibold text-slate-800">{who}</span> performed <span className="font-semibold text-slate-800">{action}</span>
        {svc && <> on <span className="font-medium">{svc}</span></>} in <span className="font-medium">{where}</span> ({when}) — <span className="font-semibold text-emerald-600">succeeded</span>.
      </>;
}

// ── Detail event (drawer) ─────────────────────────────────────────────────────
function EventDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  // identity = subset terstruktur (baru). Fallback ke raw.userIdentity untuk
  // event lama yang masih menyimpan raw penuh.
  const ui = item.identity || item.raw?.userIdentity || {};
  const failed = !!item.errorCode;

  // mfaAuthenticated bisa dari subset baru (ui.mfaAuthenticated) atau raw lama.
  const mfa = ui.mfaAuthenticated ?? ui.sessionContext?.attributes?.mfaAuthenticated;
  const isRoot = ui.type === 'Root';
  const isInternal = item.sourceIp === 'AWS Internal';
  const isPublicIp = item.sourceIp && !isInternal && !/^10\.|^192\.168\.|^172\.(1[6-9]|2\d|3[01])\./.test(item.sourceIp);

  // Baris label-value sederhana.
  const Field = ({ label, value, mono }) => (
    <div className="flex gap-3 py-1.5 border-b border-slate-50 last:border-0">
      <div className="w-32 flex-shrink-0 text-xs font-medium text-slate-400 pt-0.5">{label}</div>
      <div className={`flex-1 text-sm text-slate-700 break-words ${mono ? 'font-mono text-[12px]' : ''}`}>{value || '—'}</div>
    </div>
  );

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className={`rounded-2xl p-4 text-white shadow-sm ${failed ? 'bg-gradient-to-br from-rose-600 to-rose-700' : 'bg-gradient-to-br from-brand-700 to-brand-900'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/10 ring-1 ring-inset ring-white/15 flex items-center justify-center flex-shrink-0">
              <Zap style={{ width: 18, height: 18 }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-lg font-bold truncate">{item.eventName || '—'}</h2>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ring-1 ring-inset ${failed ? 'bg-white/15 text-white ring-white/25' : 'bg-emerald-400/15 text-emerald-300 ring-emerald-400/30'}`}>
                  {failed ? <XCircle style={{ width: 12, height: 12 }} /> : <CheckCircle2 style={{ width: 12, height: 12 }} />}
                  {failed ? t('awsTrail.failed') : t('awsTrail.success')}
                </span>
              </div>
              <p className="text-xs text-white/60 mt-0.5 truncate">
                {(item.eventSource || '').replace('.amazonaws.com', '')} · {item.region || '—'} · {fmtCell(item.eventTime, locale)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors flex-shrink-0">
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>
      {/* Narasi ringkas */}
      <div className={`px-4 py-3 rounded-xl border text-sm leading-relaxed ${failed ? 'bg-rose-50/60 border-rose-100 text-rose-900' : 'bg-brand-50/50 border-brand-100 text-slate-700'}`}>
        <div className="flex gap-2.5">
          <Info style={{ width: 16, height: 16, marginTop: 2 }} className={`flex-shrink-0 ${failed ? 'text-rose-400' : 'text-brand-400'}`} />
          <p>{buildNarrative(item, locale, id, t)}</p>
        </div>
      </div>
      {/* Error detail bila gagal */}
      {failed && (
        <div className="px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl">
          <div className="text-xs font-semibold text-rose-600 uppercase tracking-wide mb-1">{item.errorCode}</div>
          <div className="text-sm text-rose-700 leading-relaxed">{item.errorMessage || (t('awsTrail.eventFailed'))}</div>
        </div>
      )}
      {/* WHO */}
      <Section icon={UserCircle} title={t('awsTrail.who')} tone="text-violet-500">
        <div className="flex flex-wrap gap-1.5 mb-2.5">
          {isRoot
            ? <Badge icon={ShieldCheck} text="Root Account" tone="red" />
            : <Badge icon={UserCircle} text={ui.type || (t('awsTrail.user'))} tone="blue" />}
          {mfa === 'true'  && <Badge icon={Lock}     text="MFA" tone="green" />}
          {mfa === 'false' && <Badge icon={Unlock} text={t('awsTrail.noMfa')} tone="amber" />}
        </div>
        <Field label={t('awsTrail.name')} value={item.username} />
        <Field label="ARN" value={ui.arn} mono />
        <Field label="Account" value={ui.accountId} mono />
        <Field label="Principal ID" value={ui.principalId} mono />
        <Field label="Access Key" value={ui.accessKeyId} mono />
      </Section>
      {/* WHAT */}
      <Section icon={Zap} title={t('awsTrail.what')} tone="text-brand-500">
        <Field label="Event" value={item.eventName} />
        <Field label={t('awsTrail.service')} value={(item.eventSource || '').replace('.amazonaws.com', '')} />
        <Field label={t('awsTrail.time')} value={fmtCell(item.eventTime, locale)} />
        <Field label={t('awsTrail.readOnly')} value={item.readOnly ? 'Yes' : 'No'} />
        {(item.resources || []).length > 0 && (
          <div className="pt-2 mt-1">
            <div className="text-xs font-medium text-slate-400 mb-1">{t('awsTrail.affectedResources')}</div>
            {item.resources.map((r, i) => (
              <div key={i} className="text-sm text-slate-700 py-0.5">
                <span className="font-mono text-[12px]">{r.name || '—'}</span>
                {r.type && <span className="text-xs text-slate-400 ml-2">({r.type})</span>}
              </div>
            ))}
          </div>
        )}
      </Section>
      {/* WHERE */}
      <Section icon={MapPin} title={t('awsTrail.where')} tone="text-emerald-500">
        <div className="flex flex-wrap gap-1.5 mb-2.5">
          <Badge icon={Globe2} text={item.region || '—'} tone="slate" />
          {isInternal && <Badge text={id ? 'AWS Internal' : 'AWS Internal'} tone="blue" />}
          {isPublicIp && <Badge text={t('awsTrail.publicIp')} tone="amber" />}
        </div>
        <Field label="Source IP" value={item.sourceIp} mono />
        <Field label="User Agent" value={item.userAgent} mono />
        <Field label="Event ID" value={item.eventId} mono />
      </Section>
      {/* Detail terstruktur (JSON) — default tertutup. Menampilkan field
          tersimpan; raw AWS penuh tidak lagi disimpan demi hemat ukuran DB. */}
      <JsonViewer
        data={item} id={id}
        importantKeys={new Set(['errorCode', 'errorMessage'])}
        defaultCollapsed
      />
    </div>
  );
}
